import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Button,
  DatePicker,
  Modal,
  TreeSelect,
  Tooltip,
  notification,
} from 'antd'
import moment from 'moment'
import styled from 'styled-components'
import { API, expressAPI } from 'src/utils/interceptor'
import { saveAs } from 'file-saver'
import { VENUES_REPORT_DESCRIPTIONS } from './venuesReportDescriptions'
import ReportGroupSelect from './ReportGroupSelect'
import {
  canRunSupplierSpend,
  loadSuppliersInOrder,
  shouldConfirmSupplierSpendSize,
  supplierSpendLargeGroupQuestion,
  supplierSpendProgressLine,
} from './supplierSpendSuppliers'

const dateFormat = 'DD/MM/YYYY'

const normalizeSupplierTitleKey = title =>
  String(title || '')
    .trim()
    .toLowerCase()

const getFilenameFromContentDisposition = headerValue => {
  if (!headerValue) return ''
  const utf8Match = headerValue.match(/filename\*=UTF-8''([^;]+)/i)
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]).replace(/["']/g, '')
  }

  const fallbackMatch = headerValue.match(/filename="?([^"]+)"?/i)
  return fallbackMatch?.[1] || ''
}

const SupplierSpendAnalysisReport = ({ venues = [] }) => {
  const [selectedVenueIds, setSelectedVenueIds] = useState([])
  const [supplierOptions, setSupplierOptions] = useState([])
  const [selectedSupplierGroupKeys, setSelectedSupplierGroupKeys] = useState([])
  const [loadingSuppliers, setLoadingSuppliers] = useState(false)
  const [supplierProgress, setSupplierProgress] = useState('')
  const [loadingReport, setLoadingReport] = useState(false)
  const [dateRange, setDateRange] = useState([
    moment().subtract(30, 'days'),
    moment(),
  ])
  const [supplierLoadAllowedKey, setSupplierLoadAllowedKey] = useState('')
  const promptedSupplierKey = useRef('')

  const venueOptions = useMemo(
    () =>
      venues.map(item => ({
        title: item.title,
        value: item._id,
      })),
    [venues],
  )
  const allVenueIds = useMemo(
    () => venueOptions.map(({ value }) => value),
    [venueOptions],
  )

  const selectedSupplierIds = useMemo(() => {
    const ids = new Set()
    selectedSupplierGroupKeys.forEach(groupKey => {
      const option = supplierOptions.find(item => item.value === groupKey)
      ;(option?.supplierIds || []).forEach(id => ids.add(id))
    })
    return Array.from(ids)
  }, [selectedSupplierGroupKeys, supplierOptions])

  useEffect(() => {
    const selectionKey = selectedVenueIds.join('|')
    if (!selectedVenueIds.length) {
      setSupplierOptions([])
      setSelectedSupplierGroupKeys([])
      setLoadingSuppliers(false)
      setSupplierProgress('')
      return undefined
    }

    if (
      shouldConfirmSupplierSpendSize(selectedVenueIds.length) &&
      supplierLoadAllowedKey !== selectionKey
    ) {
      if (promptedSupplierKey.current === selectionKey) return undefined
      promptedSupplierKey.current = selectionKey
      Modal.confirm({
        title: 'Large supplier list',
        content: supplierSpendLargeGroupQuestion(selectedVenueIds.length),
        okText: 'Load suppliers',
        cancelText: 'Cancel',
        onOk: () => setSupplierLoadAllowedKey(selectionKey),
        onCancel: () => {
          promptedSupplierKey.current = ''
          setSelectedVenueIds([])
        },
      })
      return undefined
    }

    let cancelled = false
    const loadSuppliers = async () => {
      setLoadingSuppliers(true)
      setSupplierProgress('')
      try {
        const supplierResponses = await loadSuppliersInOrder(
          selectedVenueIds,
          venueId =>
            API.get('api', `venue/${venueId}/suppliers`, {
              queryStringParameters: { includeManualOnly: 'false' },
            }),
          (index, total) => {
            if (!cancelled)
              setSupplierProgress(supplierSpendProgressLine(index, total))
          },
        )
        if (cancelled) return

        const supplierMap = new Map()
        supplierResponses.forEach(venueSuppliers => {
          ;(venueSuppliers || []).forEach(item => {
            const supplierId = item?.supplier?._id || item?.supplier
            if (!supplierId) return

            const supplierTitle =
              item?.supplierTitle || item?.supplier?.title || 'Unknown Supplier'
            const supplierKey = normalizeSupplierTitleKey(supplierTitle)
            if (!supplierKey) return

            if (!supplierMap.has(supplierKey)) {
              supplierMap.set(supplierKey, {
                title: supplierTitle,
                value: supplierKey,
                supplierIds: [],
              })
            }

            const current = supplierMap.get(supplierKey)
            if (!current.supplierIds.includes(supplierId)) {
              current.supplierIds.push(supplierId)
            }
          })
        })

        const dedupedSuppliers = Array.from(supplierMap.values()).sort((a, b) =>
          a.title.localeCompare(b.title),
        )

        setSupplierOptions(dedupedSuppliers)
        setSelectedSupplierGroupKeys(prev =>
          prev.filter(key =>
            dedupedSuppliers.some(option => option.value === key),
          ),
        )
      } catch (error) {
        if (cancelled) return
        console.error('Failed to load suppliers for selected venues:', error)
        notification.error({
          message: 'Unable to load suppliers',
          description:
            'We could not load suppliers for the selected venues. Please try again.',
        })
        setSupplierOptions([])
        setSelectedSupplierGroupKeys([])
      } finally {
        if (!cancelled) setLoadingSuppliers(false)
        if (!cancelled) setSupplierProgress('')
      }
    }

    loadSuppliers()
    return () => {
      cancelled = true
    }
  }, [selectedVenueIds, supplierLoadAllowedKey])

  const runReport = async () => {
    if (
      !selectedVenueIds.length ||
      !selectedSupplierGroupKeys.length ||
      !dateRange?.[0] ||
      !dateRange?.[1]
    ) {
      return
    }

    setLoadingReport(true)
    try {
      const start = dateRange[0].format('YYYY-MM-DD')
      const end = dateRange[1].format('YYYY-MM-DD')

      const response = await expressAPI.post(
        'venues/supplier-spend-analysis',
        {
          venueIds: selectedVenueIds,
          supplierIds: selectedSupplierIds,
          start,
          end,
        },
        { responseType: 'text' },
      )

      const filename =
        getFilenameFromContentDisposition(
          response.headers?.['content-disposition'],
        ) || `supplier-spend-analysis_${start}_to_${end}.csv`

      const blob = new Blob([response.data || ''], {
        type: 'text/csv;charset=utf-8;',
      })
      saveAs(blob, filename)

      notification.success({
        message: 'Report generated',
        description: 'Supplier Spend Analysis CSV downloaded successfully.',
      })
    } catch (error) {
      console.error('Failed to download Supplier Spend Analysis report:', error)
      notification.error({
        message: 'Report failed',
        description:
          'We could not generate the Supplier Spend Analysis report. Please try again.',
      })
    } finally {
      setLoadingReport(false)
    }
  }

  const canRunReport = canRunSupplierSpend({
    venueCount: selectedVenueIds.length,
    supplierCount: selectedSupplierIds.length,
    loadingSuppliers,
    hasDates: Boolean(dateRange?.[0] && dateRange?.[1]),
  })

  return (
    <StyledBlock>
      <HelperText data-testid='report-helper'>
        {VENUES_REPORT_DESCRIPTIONS['Supplier Spend Analysis']}
      </HelperText>

      <ReportGroupSelect venues={venues} onPick={setSelectedVenueIds} />
      <TreeSelect
        treeNodeFilterProp='title'
        allowClear
        placeholder='Select Venues ...'
        treeCheckable
        showCheckedStrategy={TreeSelect.SHOW_CHILD}
        dropdownStyle={{ maxHeight: '300px' }}
        style={{ marginBottom: '15px', width: '355px' }}
        onChange={ids => setSelectedVenueIds(ids)}
        value={selectedVenueIds}
        treeData={[
          {
            title:
              selectedVenueIds.length > 0 ? (
                <span
                  onClick={() => setSelectedVenueIds([])}
                  style={{
                    display: 'inline-block',
                    color: '#b5d922',
                    cursor: 'pointer',
                  }}
                >
                  Unselect all
                </span>
              ) : (
                <span
                  onClick={() => setSelectedVenueIds(allVenueIds)}
                  style={{
                    display: 'inline-block',
                    color: '#b5d922',
                    cursor: 'pointer',
                  }}
                >
                  Select all
                </span>
              ),
            value: 'select-all-venues',
            disableCheckbox: true,
            disabled: true,
          },
          ...venueOptions,
        ]}
      />

      {supplierProgress ? <p>{supplierProgress}</p> : null}

      {selectedVenueIds.length > 0 && (
        <TreeSelect
          treeNodeFilterProp='title'
          allowClear
          placeholder={
            loadingSuppliers ? 'Loading suppliers...' : 'Select Suppliers ...'
          }
          treeCheckable
          showCheckedStrategy={TreeSelect.SHOW_CHILD}
          dropdownStyle={{ maxHeight: '300px' }}
          style={{ marginBottom: '15px', width: '355px' }}
          onChange={keys => setSelectedSupplierGroupKeys(keys)}
          value={selectedSupplierGroupKeys}
          maxTagCount={1}
          maxTagPlaceholder={omittedValues =>
            `+ ${omittedValues.length} Suppliers ...`
          }
          loading={loadingSuppliers}
          disabled={loadingSuppliers || supplierOptions.length === 0}
          treeData={supplierOptions}
          notFoundContent={
            loadingSuppliers
              ? 'Loading suppliers...'
              : 'No suppliers available for selected venues'
          }
        />
      )}

      {selectedSupplierGroupKeys.length > 0 && (
        <StyledRangePicker
          value={dateRange}
          onChange={value => setDateRange(value)}
          format={dateFormat}
          style={{ width: '355px', marginBottom: '15px' }}
        />
      )}

      {!canRunReport ? (
        <Tooltip title='Select venues, suppliers, and a date range to run this report.'>
          <span
            style={{ display: 'inline-block', width: '355px', marginTop: 20 }}
          >
            <Button style={{ width: '100%' }} disabled>
              Run Report
            </Button>
          </span>
        </Tooltip>
      ) : (
        <Button
          loading={loadingReport}
          style={{ width: '355px', marginTop: 20 }}
          className='ant-btn ant-btn-primary'
          onClick={runReport}
          disabled={loadingReport}
        >
          Run Report
        </Button>
      )}
    </StyledBlock>
  )
}

const StyledBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const HelperText = styled.p`
  font-size: 14px;
  color: #666;
  margin-bottom: 10px;
`

const StyledRangePicker = styled(DatePicker.RangePicker)`
  .ant-calendar-picker-input.ant-input {
    height: 40px;
    padding: 6px 6px;
    font-size: 16px;
  }
`

export default SupplierSpendAnalysisReport
